import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface PortfolioHolding {
  id: string;
  user_id: string;
  broker_connection_id: string | null;
  symbol: string;
  asset_name: string | null;
  asset_type: string;
  quantity: number;
  average_price: number;
  current_price: number | null;
  market_value: number | null;
  unrealized_pnl: number | null;
  unrealized_pnl_pct: number | null;
  sector: string | null;
  exchange: string | null;
  currency: string | null;
  source: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

interface AddHoldingInput {
  symbol: string;
  asset_type: string;
  quantity: number;
  average_price: number;
  asset_name?: string;
  sector?: string;
  source?: string;
}

const DEMO_HOLDINGS: AddHoldingInput[] = [
  { symbol: 'AAPL', quantity: 10, average_price: 178.50, asset_type: 'stock', sector: 'Technology', asset_name: 'Apple Inc.' },
  { symbol: 'MSFT', quantity: 5, average_price: 420.00, asset_type: 'stock', sector: 'Technology', asset_name: 'Microsoft Corp.' },
  { symbol: 'TSLA', quantity: 8, average_price: 245.00, asset_type: 'stock', sector: 'Consumer Discretionary', asset_name: 'Tesla Inc.' },
  { symbol: 'BTC/USD', quantity: 0.15, average_price: 62000, asset_type: 'crypto', sector: 'Crypto', asset_name: 'Bitcoin' },
  { symbol: 'ETH/USD', quantity: 2.5, average_price: 3200, asset_type: 'crypto', sector: 'Crypto', asset_name: 'Ethereum' },
  { symbol: 'XAU/USD', quantity: 1, average_price: 2100, asset_type: 'commodity', sector: 'Commodities', asset_name: 'Gold' },
  { symbol: 'EUR/USD', quantity: 1000, average_price: 1.08, asset_type: 'forex', sector: 'Forex', asset_name: 'Euro/USD' },
  { symbol: 'JPM', quantity: 15, average_price: 195.00, asset_type: 'stock', sector: 'Financials', asset_name: 'JPMorgan Chase' },
  { symbol: 'XOM', quantity: 20, average_price: 108.00, asset_type: 'stock', sector: 'Energy', asset_name: 'Exxon Mobil' },
  { symbol: 'JNJ', quantity: 12, average_price: 156.00, asset_type: 'stock', sector: 'Healthcare', asset_name: 'Johnson & Johnson' },
];

export function usePortfolioHoldings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const seedingRef = useRef(false);

  const holdingsQuery = useQuery({
    queryKey: ['portfolio-holdings', user?.id],
    queryFn: async (): Promise<PortfolioHolding[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('portfolio_holdings')
        .select('*')
        .eq('user_id', user.id)
        .order('market_value', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data || []) as PortfolioHolding[];
    },
    enabled: !!user,
    staleTime: 30000,
  });

  // Demo portfolio seeding
  useEffect(() => {
    if (
      !user ||
      holdingsQuery.isLoading ||
      holdingsQuery.data === undefined ||
      holdingsQuery.data.length > 0 ||
      seedingRef.current
    ) return;

    const key = `demo_portfolio_seeded_${user.id}`;
    if (localStorage.getItem(key)) return;

    seedingRef.current = true;
    const rows = DEMO_HOLDINGS.map(h => ({
      user_id: user.id,
      symbol: h.symbol,
      asset_type: h.asset_type,
      quantity: h.quantity,
      average_price: h.average_price,
      asset_name: h.asset_name || null,
      sector: h.sector || 'Other',
      source: 'manual' as const,
    }));

    supabase
      .from('portfolio_holdings')
      .insert(rows)
      .then(({ error }) => {
        if (!error) {
          localStorage.setItem(key, 'true');
          queryClient.invalidateQueries({ queryKey: ['portfolio-holdings'] });
        }
        seedingRef.current = false;
      });
  }, [user, holdingsQuery.isLoading, holdingsQuery.data, queryClient]);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('portfolio-holdings-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'portfolio_holdings', filter: `user_id=eq.${user.id}` }, () => {
        queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  const isDemoPortfolio = useMemo(() => {
    if (!user || !holdingsQuery.data) return false;
    const key = `demo_portfolio_seeded_${user.id}`;
    return localStorage.getItem(key) === 'true' && holdingsQuery.data.every(h => h.source === 'manual');
  }, [user, holdingsQuery.data]);

  const derived = useMemo(() => {
    const holdings = holdingsQuery.data || [];
    const totalValue = holdings.reduce((sum, h) => sum + (h.market_value || 0), 0);
    const totalInvested = holdings.reduce((sum, h) => sum + h.average_price * h.quantity, 0);
    const totalPnl = totalValue - totalInvested;
    const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0;

    const sectorMap: Record<string, number> = {};
    holdings.forEach(h => {
      const sector = h.sector || 'Other';
      sectorMap[sector] = (sectorMap[sector] || 0) + (h.market_value || 0);
    });
    const sectorBreakdown = Object.entries(sectorMap).map(([sector, value]) => ({
      sector,
      value,
      percentage: totalValue > 0 ? (value / totalValue) * 100 : 0,
    }));

    return { totalValue, totalInvested, totalPnl, totalPnlPct, sectorBreakdown };
  }, [holdingsQuery.data]);

  const addHolding = useMutation({
    mutationFn: async (input: AddHoldingInput) => {
      if (!user) throw new Error('Not authenticated');

      // Look up sector
      let sector = input.sector || 'Other';
      if (!input.sector) {
        const { data: sc } = await supabase
          .from('sector_classifications')
          .select('sector')
          .eq('symbol', input.symbol)
          .maybeSingle();
        if (sc) sector = sc.sector;
      }

      // Fetch current price
      let currentPrice = input.average_price;
      const { data: mp } = await supabase
        .from('market_prices')
        .select('price')
        .eq('symbol', input.symbol)
        .maybeSingle();
      if (mp) currentPrice = mp.price;

      const { error } = await supabase.from('portfolio_holdings').insert({
        user_id: user.id,
        symbol: input.symbol,
        asset_type: input.asset_type,
        quantity: input.quantity,
        average_price: input.average_price,
        current_price: currentPrice,
        asset_name: input.asset_name || null,
        sector,
        source: input.source || 'manual',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings'] });
      toast({ title: 'Holding added', description: 'Your holding has been added successfully.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const deleteHolding = useMutation({
    mutationFn: async (holdingId: string) => {
      const { error } = await supabase.from('portfolio_holdings').delete().eq('id', holdingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings'] });
    },
  });

  const upsertBatch = useCallback(async (holdings: AddHoldingInput[]) => {
    if (!user) throw new Error('Not authenticated');

    const { data: sectors } = await supabase.from('sector_classifications').select('symbol, sector');
    const sectorMap = new Map((sectors || []).map(s => [s.symbol, s.sector]));

    const rows = holdings.map(h => ({
      user_id: user.id,
      symbol: h.symbol,
      asset_type: h.asset_type,
      quantity: h.quantity,
      average_price: h.average_price,
      asset_name: h.asset_name || null,
      sector: h.sector || sectorMap.get(h.symbol) || 'Other',
      source: h.source || 'csv_import',
    }));

    const { error } = await supabase.from('portfolio_holdings').insert(rows);
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ['portfolio-holdings'] });
    return rows.length;
  }, [user, queryClient]);

  return {
    holdings: holdingsQuery.data || [],
    isLoading: holdingsQuery.isLoading,
    isDemoPortfolio,
    ...derived,
    addHolding,
    deleteHolding,
    upsertBatch,
  };
}
